import type { ServicePointCalibMetadata } from 'api/RobotApi'
import { ROBOT_MODES } from 'utils/constants'
import type { ServiceCalibAwaitOutcome } from 'utils/interfaces'
import { LOT_TYPE } from 'utils/lotInterfaces'

import type { ApiLotRow, ApiMaintenancePointRow, ApiStationRow } from './serviceCalibApi'
import type {
  MaintenancePointKind,
  ServiceCalibCatalogData,
  ServiceCalibCatalogItem,
  ServiceCalibStationGroup,
  ServicePointCalibrationStatus,
} from './serviceCalibTypes'

/** ServicePoint.msg kind codes — same as OperatingMode.metadata.kind */
export const KIND_STATION_LOT = 2
export const KIND_MAINTENANCE_POINT = 3

/** ServicePoint.msg action codes */
export const ACTION_PARKING = 2
export const ACTION_WASHING = 3
export const ACTION_CHARGING = 4
export const ACTION_REFILLING = 5
export const ACTION_WASTE_WATER_DRAIN = 6
export const ACTION_DEBRIS_DUMP = 7

function normalizeMaintenancePointType(type: string | undefined): MaintenancePointKind | undefined {
  if (type === 'waste_water_drain' || type === 'debris_dump' || type === 'universal') {
    return type
  }
  return undefined
}

function lotItem(
  lot: ApiLotRow,
  station: ApiStationRow | undefined,
  hardwareId: string
): ServiceCalibCatalogItem | null {
  const lotModel = lot.lot_model?.trim()
  if (!lotModel) {
    return null
  }
  const metadata: ServicePointCalibMetadata = {
    kind: KIND_STATION_LOT,
    station_id: hardwareId,
    lot_id: lot.lot_id,
    lot_model: lotModel,
  }
  return {
    key: `lot-${lot.id}`,
    name: lot.name,
    target: 'station_lot',
    point: station?.point ?? null,
    metadata,
    stationHardwareId: hardwareId,
    stationName: station?.name,
    lotId: lot.lot_id,
    lotType: lot.type,
  }
}

function maintenancePointItem(point: ApiMaintenancePointRow): ServiceCalibCatalogItem | null {
  if (point.facility_index == null) {
    return null
  }
  const metadata: ServicePointCalibMetadata = {
    kind: KIND_MAINTENANCE_POINT,
    maintenance_point_id: point.facility_index,
  }
  return {
    key: `mp-${point.id}`,
    name: point.name,
    target: 'maintenance_point',
    point: point.point ?? null,
    metadata,
    maintenancePointId: point.facility_index,
    maintenancePointType: normalizeMaintenancePointType(point.type),
  }
}

export function buildServiceCalibCatalog(
  lots: ApiLotRow[],
  maintenancePoints: ApiMaintenancePointRow[],
  stations: ApiStationRow[]
): ServiceCalibCatalogData {
  const stationByHardwareId = new Map<string, ApiStationRow>()
  stations.forEach(station => {
    if (station.station_id) {
      stationByHardwareId.set(station.station_id, station)
    }
  })

  const reportedByHardwareId = new Map<string, Set<number> | null>()
  stations.forEach(station => {
    if (!station.station_id) return
    // MR-05b: when station expects telemetry and has a reported set, operational calib
    // shows only reported lots. Empty reported → keep all configured (first-time setup).
    // UX-STATION-NF: passive stations never filter by reported.
    if (station.expects_telemetry === false) {
      reportedByHardwareId.set(station.station_id, null)
      return
    }
    const reported = Array.isArray(station.reported_lot_ids) ? station.reported_lot_ids : []
    reportedByHardwareId.set(station.station_id, reported.length > 0 ? new Set(reported) : null)
  })

  const lotsByStation = new Map<string, ApiLotRow[]>()
  lots.forEach(lot => {
    if (lot.type === LOT_TYPE.NOT_SET) {
      return
    }
    const hardwareId = lot.robot_station_hardware_id?.trim()
    if (!hardwareId) {
      return
    }
    const reportedFilter = reportedByHardwareId.get(hardwareId)
    if (reportedFilter && !reportedFilter.has(lot.lot_id)) {
      return
    }
    const group = lotsByStation.get(hardwareId) ?? []
    group.push(lot)
    lotsByStation.set(hardwareId, group)
  })

  const stationGroups: ServiceCalibStationGroup[] = []
  lotsByStation.forEach((stationLots, hardwareId) => {
    const station = stationByHardwareId.get(hardwareId)
    const lotItems = stationLots
      .map(lot => lotItem(lot, station, hardwareId))
      .filter((item): item is ServiceCalibCatalogItem => item != null)
      .sort((a, b) => {
        const idA = a.lotId ?? 0
        const idB = b.lotId ?? 0
        if (idA !== idB) {
          return idA - idB
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      })
    if (!lotItems.length) {
      return
    }
    stationGroups.push({
      stationKey: `station-${hardwareId}`,
      stationName: station?.name || hardwareId,
      stationHardwareId: hardwareId,
      stationPoint: station?.point ?? null,
      lots: lotItems,
    })
  })

  stationGroups.sort((a, b) => a.stationName.localeCompare(b.stationName, undefined, { sensitivity: 'base' }))

  const maintenanceItems = maintenancePoints
    .map(maintenancePointItem)
    .filter((item): item is ServiceCalibCatalogItem => item != null)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

  const mapItems = [...stationGroups.flatMap(g => g.lots), ...maintenanceItems].filter(
    item => item.point?.type === 'Point' && item.point.coordinates?.length === 2
  )

  return {
    stationGroups,
    maintenancePoints: maintenanceItems,
    mapItems,
  }
}

export function buildServiceCalibCatalogItems(
  lots: ApiLotRow[],
  maintenancePoints: ApiMaintenancePointRow[],
  stations: ApiStationRow[]
): ServiceCalibCatalogItem[] {
  const data = buildServiceCalibCatalog(lots, maintenancePoints, stations)
  return [...data.stationGroups.flatMap(g => g.lots), ...data.maintenancePoints]
}

/** Service-point calibration requires lidar map + LOCALIZED status. */
export function isRobotLidarLocalizedForServicePointCalib(
  localizationInfo:
    | {
        localization_status?: boolean
        current_lidar_map?: string
      }
    | undefined
    | null
): boolean {
  const currentLidarMap = localizationInfo?.current_lidar_map?.trim()
  return Boolean(localizationInfo?.localization_status && currentLidarMap)
}

type CatalogRowButtonHintContext = {
  isOnline: boolean
  loading: boolean
  ready?: boolean
  lidarLocalized?: boolean
  modeInProgress?: boolean
  blockedByOtherOperation?: boolean
}

export function getCalibrateButtonTitle(ctx: CatalogRowButtonHintContext & { calibFailed?: boolean }): string {
  if (ctx.loading) {
    return 'Waiting for calibration mode…'
  }
  if (ctx.calibFailed) {
    return 'Calibration failed — try again'
  }
  if (ctx.ready) {
    return 'Already calibrated'
  }
  if (!ctx.lidarLocalized) {
    return 'Robot must be localized on a lidar map'
  }
  if (!ctx.isOnline) {
    return 'Robot is offline'
  }
  if (ctx.modeInProgress) {
    return 'Robot is busy with another task'
  }
  if (ctx.blockedByOtherOperation) {
    return 'Another service command is in progress'
  }
  return 'Calibrate'
}

export function getFinishCalibButtonTitle(ctx: CatalogRowButtonHintContext & { calibFailed?: boolean }): string {
  if (ctx.loading) {
    return 'Waiting for calibrated point in telemetry…'
  }
  if (ctx.calibFailed) {
    return 'Calibration failed — try again'
  }
  if (!ctx.isOnline) {
    return 'Robot is offline'
  }
  if (ctx.modeInProgress) {
    return 'Robot is busy with another task'
  }
  if (ctx.blockedByOtherOperation) {
    return 'Another service command is in progress'
  }
  return 'Finish calibration'
}

export function getResetCalibButtonTitle(ctx: CatalogRowButtonHintContext): string {
  if (ctx.loading) {
    return 'Waiting for robot response…'
  }
  if (!ctx.ready) {
    return 'Calibrate this service point first'
  }
  if (!ctx.isOnline) {
    return 'Robot is offline'
  }
  if (ctx.modeInProgress) {
    return 'Robot is busy with another task'
  }
  if (ctx.blockedByOtherOperation) {
    return 'Another service command is in progress'
  }
  return 'Reset calibration'
}

type CatalogServiceButtonHintContext = CatalogRowButtonHintContext & {
  activeOnRow: boolean
  actionLabel: string
  serviceModeUnavailable?: boolean
  needsActionSelection?: boolean
}

export function getCatalogServiceButtonTitle(ctx: CatalogServiceButtonHintContext): string {
  if (ctx.needsActionSelection) {
    return 'Choose actions'
  }
  if (ctx.activeOnRow) {
    if (ctx.loading) {
      return 'Waiting for robot response…'
    }
    if (!ctx.isOnline) {
      return 'Robot is offline'
    }
    return 'Stop'
  }
  if (ctx.loading) {
    return 'Waiting for robot response…'
  }
  if (ctx.serviceModeUnavailable) {
    return 'Service kind is not available on this robot'
  }
  if (!ctx.ready) {
    return 'Calibrate this service point first'
  }
  if (!ctx.isOnline) {
    return 'Robot is offline'
  }
  if (ctx.modeInProgress) {
    return 'Robot is busy with another task'
  }
  if (ctx.blockedByOtherOperation) {
    return 'Another service command is in progress'
  }
  return ctx.actionLabel
}

function findServiceCalibRow(
  item: Pick<ServiceCalibCatalogItem, 'target' | 'stationHardwareId' | 'lotId' | 'maintenancePointId'>,
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null
): ServicePointCalibrationStatus | undefined {
  if (!calibrationRows?.length) {
    return undefined
  }
  if (item.target === 'station_lot') {
    return calibrationRows.find(
      row =>
        (row.kind === KIND_STATION_LOT || row.target === 'station_lot') &&
        String(row.station_id ?? '') === String(item.stationHardwareId ?? '') &&
        Number(row.lot_id ?? 0) === Number(item.lotId ?? 0)
    )
  }
  return calibrationRows.find(
    row =>
      (row.kind === KIND_MAINTENANCE_POINT || row.target === 'maintenance_point') &&
      Number(row.maintenance_point_id ?? 0) === Number(item.maintenancePointId ?? 0)
  )
}

/** Protocol ready (mode 0) — used for settle logic and command outcome. */
export function isServiceCalibItemReady(
  item: Pick<ServiceCalibCatalogItem, 'target' | 'stationHardwareId' | 'lotId' | 'maintenancePointId'>,
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null
): boolean {
  const row = findServiceCalibRow(item, calibrationRows)
  return Boolean(row?.ready)
}

/** UI calibrated state — unchanged during reset (16) or service (17). */
export function isServiceCalibItemCalibrated(
  item: Pick<ServiceCalibCatalogItem, 'target' | 'stationHardwareId' | 'lotId' | 'maintenancePointId'>,
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null
): boolean {
  const row = findServiceCalibRow(item, calibrationRows)
  if (!row) {
    return false
  }
  if (row.ready) {
    return true
  }
  const mode = row.operating_mode
  return mode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT || mode === ROBOT_MODES.MODE_SERVICE_POINT
}

export type ServiceCalibGroupStatus = 'all_ready' | 'none_ready' | 'mixed'

export function getServiceCalibGroupStatus(
  items: ServiceCalibCatalogItem[],
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null
): ServiceCalibGroupStatus {
  if (!items.length) {
    return 'none_ready'
  }
  const readyCount = items.filter(item => isServiceCalibItemCalibrated(item, calibrationRows)).length
  if (readyCount === 0) {
    return 'none_ready'
  }
  if (readyCount === items.length) {
    return 'all_ready'
  }
  return 'mixed'
}

export function serviceCalibAwaitOutcomeMatches(
  awaitCalib: ServiceCalibAwaitOutcome,
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null
): boolean {
  // null/undefined = telemetry not loaded yet; do not treat as "point absent" for reset/finish.
  if (calibrationRows == null) {
    return false
  }
  const ready = isServiceCalibItemReady(awaitCalib, calibrationRows)
  return ready === awaitCalib.expectedReady
}

const MODE_NOT_SET = 0

export type OnboardServicePointWire = {
  kind?: number
  station_id?: string
  lot_id?: number
  lot_model?: string
  maintenance_point_id?: number
}

type ActiveModeMetadataWire = {
  kind?: number
  station_id?: string
  lot_id?: number
  maintenance_point_id?: number
}

function parseOnboardServicePointRow(point: OnboardServicePointWire): ServicePointCalibrationStatus | null {
  const kind = Number(point.kind)
  if (kind === KIND_STATION_LOT) {
    return {
      kind: KIND_STATION_LOT,
      target: 'station_lot',
      station_id: String(point.station_id ?? ''),
      lot_id: Number(point.lot_id ?? 0),
      ready: true,
      operating_mode: MODE_NOT_SET,
    }
  }
  if (kind === KIND_MAINTENANCE_POINT) {
    const maintenancePointId = Number(point.maintenance_point_id ?? 0)
    if (!maintenancePointId) {
      return null
    }
    return {
      kind: KIND_MAINTENANCE_POINT,
      target: 'maintenance_point',
      maintenance_point_id: maintenancePointId,
      ready: true,
      operating_mode: MODE_NOT_SET,
    }
  }
  return null
}

/** Mirror API parse_onboard_service_points — presence in array = calibrated onboard. */
export function parseOnboardServicePoints(
  points: OnboardServicePointWire[] | undefined | null
): ServicePointCalibrationStatus[] | null {
  if (points == null) {
    return null
  }
  const normalized: ServicePointCalibrationStatus[] = []
  for (const point of points) {
    const row = parseOnboardServicePointRow(point)
    if (row) {
      normalized.push(row)
    }
  }
  return normalized
}

function metadataMatchesRow(metadata: ActiveModeMetadataWire, row: ServicePointCalibrationStatus): boolean {
  const kind = Number(metadata.kind)
  if (kind === KIND_STATION_LOT || row.target === 'station_lot') {
    return (
      row.target === 'station_lot' &&
      String(metadata.station_id ?? '') === String(row.station_id ?? '') &&
      Number(metadata.lot_id ?? 0) === Number(row.lot_id ?? 0)
    )
  }
  if (kind === KIND_MAINTENANCE_POINT || row.target === 'maintenance_point') {
    return (
      row.target === 'maintenance_point' &&
      Number(metadata.maintenance_point_id ?? 0) === Number(row.maintenance_point_id ?? 0)
    )
  }
  return false
}

function enrichCalibrationRowsWithActiveMode(
  rows: ServicePointCalibrationStatus[],
  activeMode: ROBOT_MODES | null | undefined,
  activeModeMetadata: ActiveModeMetadataWire | null | undefined
): ServicePointCalibrationStatus[] {
  if (
    activeMode == null ||
    !activeModeMetadata ||
    (activeMode !== ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
      activeMode !== ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT &&
      activeMode !== ROBOT_MODES.MODE_SERVICE_POINT)
  ) {
    return rows
  }
  return rows.map(row => (metadataMatchesRow(activeModeMetadata, row) ? { ...row, operating_mode: activeMode } : row))
}

/** Source of truth: Telemetry.onboard_service_points[] (+ active_mode metadata for in-flight ops). */
export function getEffectiveServicePointCalibrationRows(
  telemetry:
    | {
        onboardServicePoints?: OnboardServicePointWire[] | null
      }
    | null
    | undefined,
  options?: {
    activeMode?: ROBOT_MODES | null
    activeModeMetadata?: ActiveModeMetadataWire | null
  }
): ServicePointCalibrationStatus[] | undefined | null {
  if (!telemetry) {
    return undefined
  }
  if (!Array.isArray(telemetry.onboardServicePoints)) {
    return undefined
  }
  const rows = parseOnboardServicePoints(telemetry.onboardServicePoints) ?? []
  return enrichCalibrationRowsWithActiveMode(rows, options?.activeMode, options?.activeModeMetadata)
}
